import { Dispatch, SetStateAction } from 'react'

import useSWR from 'swr'

import { useCallMutation } from '@/common/api/client'
import { apiDefaultFetcher, apiDeleteFetcher } from '@/common/api/fetcher'
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import {
  BulkCreateOrderHistoryResponse,
  BulkCreateOrderHistoryVars,
  DeleteOrderHistoryResponse,
  DeleteOrderHistoryVars,
  ListOrderHistoryResponse,
  ListOrderHistoryVars,
  OrderHistoryApiPath,
  OrderItemRankingsAnalysisResponse,
  OrderItemRankingsAnalysisVars,
  OrderItemRankingsResponse,
  OrderItemRankingsVars
} from '@/common/api/params/orderHistory'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'

export interface OrderHistorySearchCondition extends SearchConditionBase {
  keyword: string | null
  orderDatetime: string | null
  customerId: string | null
  orderNumber: string | null
  itemId: string | null
}

const orderHistorySearchConditionParam: SearchConditionParam[] = [
  { name: 'keyword', type: 'string' },
  { name: 'orderDatetime', type: 'string' },
  { name: 'customerId', type: 'string' },
  { name: 'orderNumber', type: 'string' },
  { name: 'itemId', type: 'string' }
]

export const ORDER_HISTORY_SEARCH_CONDITION_CLEAR: OrderHistorySearchCondition = {
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PER_PAGE,
  keyword: null,
  orderDatetime: null,
  customerId: null,
  orderNumber: null,
  itemId: null
}

export const useOrderHistorySearchCondition = (): [
  OrderHistorySearchCondition,
  Dispatch<SetStateAction<OrderHistorySearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<OrderHistorySearchCondition>(orderHistorySearchConditionParam)
}

export const convertConditionToOrderHistoryVars = (
  siteId: number,
  condition: OrderHistorySearchCondition
): ListOrderHistoryVars => ({
  site_id: siteId,
  page: condition.page,
  per_page: condition.perPage,
  ...(condition.keyword && { search: condition.keyword }),
  ...(condition.orderDatetime && { s_order_datetime: condition.orderDatetime }),
  ...(condition.customerId && { s_customer_id: condition.customerId }),
  ...(condition.orderNumber && { s_order_number: condition.orderNumber }),
  ...(condition.itemId && { s_item_id: condition.itemId })
})

export const useOrderHistoryListApi = (vars: ListOrderHistoryVars | null) => {
  return useSWR<ListOrderHistoryResponse, Response, [string, ListOrderHistoryVars] | null>(
    vars ? [OrderHistoryApiPath.LIST_ORDER_HISTORY, vars] : null,
    apiDefaultFetcher
  )
}

export const useOrderItemRankingsApi = (vars: OrderItemRankingsVars | null) => {
  return useSWR<OrderItemRankingsResponse, Response, [string, OrderItemRankingsVars] | null>(
    vars ? [OrderHistoryApiPath.ORDER_ITEM_RANKINGS, vars] : null,
    apiDefaultFetcher
  )
}

export const useDeleteOrderHistoryMutation = (options?: {
  onSuccess?: (response: DeleteOrderHistoryResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<DeleteOrderHistoryResponse, DeleteOrderHistoryVars>(
    OrderHistoryApiPath.DELETE_ORDER_HISTORY,
    options,
    apiDeleteFetcher
  )
}

export const useBulkCreateOrderHistoryMutation = (options?: {
  onSuccess?: (response: BulkCreateOrderHistoryResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<BulkCreateOrderHistoryResponse, BulkCreateOrderHistoryVars>(
    OrderHistoryApiPath.BULK_CREATE_ORDER_HISTORY,
    options
  )
}

export const useOrderItemRankingsAnalysisMutation = (options?: {
  onSuccess?: (response: OrderItemRankingsAnalysisResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<OrderItemRankingsAnalysisResponse, OrderItemRankingsAnalysisVars>(
    OrderHistoryApiPath.ORDER_ITEM_RANKINGS_ANALYSIS,
    options
  )
}
